import { computed, Injectable, Signal, signal } from '@angular/core';
import { PiramidalService } from '../../core/services/piramidal.service';
import { ProductCartClass } from '../interfaces/productCart.class';
import { Product } from '@core/models/piramide/Product';
import { PercepcionesCalculadas } from '@core/models/percepcionesCalculadas';
import { Discount } from '@core/services/DiscountHandler/Model/Discount';
import { DiscountCartClass } from '../interfaces/discountCart.class';
import { first, map, Observable, of } from 'rxjs';
import {
  idInstalarCCPU,
  idsArmados,
  SubcatesObligatorias,
} from 'src/app/arma-pc/services/componentesObligatoriosArmado';
import { DialogModalComponent } from '@core/components/dialog-modal/dialog-modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SubcategoriesEnum } from '@core/enums/subcategories.enum';
import { HttpClient } from '@angular/common/http';
import { NavigationService } from '@core/services/navigation.service';
import { httpError } from '@core/models/HttpServiceResponse';
import { ModalMessageService } from '@core/services/modal-message.service';
import { NotificationVariant } from '@core/enums/notifications/notification-variant.enum';

export interface IProductCart {
  id?: number;
  idBuild?: number;
  idProduct: number;
  quantity: number;
}
@Injectable({
  providedIn: 'root',
})
export class ShoppingCartService {
  private readonly cartElements = signal<ProductCartClass[]>([]);
  private readonly shipmentPrice = signal<number>(null);
  private readonly idTypePayment = signal<number>(3);
  private readonly discount = signal<DiscountCartClass>(null);
  private readonly discountParam = signal<{ id: number; code: string }>(null);
  private readonly perceptions = signal<PercepcionesCalculadas[]>(null);
  public readonly totalPrice = computed(() => {
    const totalPriceShipment = this.shipmentPrice() || 0;
    const totalPriceProducts = this.cartElements()?.reduce(
      (sum, cartElement) => sum + cartElement.totalPrice(),
      0,
    );
    const totalPriceBilling =
      this.perceptions()?.reduce((sum, billing) => sum + billing.total, 0) || 0;
    return totalPriceShipment + totalPriceProducts + totalPriceBilling;
  });

  private readonly bienDeUso = signal<boolean>(false);
  private readonly _idModality = signal<number>(null);
  public readonly idModality = this._idModality.asReadonly();

  private readonly _postalCode = signal<string>(null);
  public readonly postalCode = this._postalCode.asReadonly();

  constructor(
    private _piramidalService: PiramidalService,
    private dialogRef: MatDialogRef<DialogModalComponent>,
    private dialog: MatDialog,
    private _http: HttpClient,
    private navigationService: NavigationService,
    private modalMessageService: ModalMessageService,
  ) {}

  public addProduct(idProduct: number): void {
    const existProduct = this.cartElements().find(
      (cartElement) => cartElement.idProduct == idProduct && !cartElement.idBuild,
    );
    if (existProduct) {
      existProduct.addOneQuantity();
    } else {
      this.setCartElements([{ idProduct, quantity: 1 }]);
    }
  }

  public setCartElements(cartProducts: IProductCart[]): void {
    const elementsMap = new Map(this.cartElements().map((el) => [`${el.idProduct}`, el]));
    if (cartProducts.length) {
      cartProducts.forEach((cartProduct) => {
        const key = `${cartProduct.idProduct}`;
        let existProduct = elementsMap.get(key);
        if (existProduct && cartProduct.quantity !== existProduct.quantity()) {
          existProduct.changeQuantity(cartProduct.quantity);
        } else if (!existProduct) {
          this._piramidalService.ProductDetails.observable.subscribe((res) => {
            if (res.length) {
              const product = res.find((p) => p?.id_producto == cartProduct.idProduct).$product;
              if (product) {
                let productCart = new ProductCartClass(product, cartProduct.quantity);
                this.cartElements.update((cart) => [...cart, productCart]);
              }
            }
          });
        }
      });
    } else {
      this.cartElements.set([]);
    }
    // TODO: descomentar para aplicar la lógica con builds

    // const elementsMap = new Map(
    //   this.cartElements().map((el) => [`${el.idBuild ?? ''}-${el.idProduct}`, el]),
    // );

    // cartProducts.forEach((cartProduct) => {
    //   const key = `${cartProduct.idBuild ?? ''}-${cartProduct.idProduct}`;
    //   let existProduct = elementsMap.get(key);

    //   if (existProduct && cartProduct.quantity !== existProduct.quantity()) {
    //     existProduct.changeQuantity(cartProduct.quantity);
    //   } else if (!existProduct) {
    //     const product = this.products().find((p) => p?.id_producto == cartProduct.idProduct);
    //     if (product) {
    //       let productCart = new ProductCartClass(
    //         product,
    //         cartProduct.quantity,
    //         cartProduct.idBuild,
    //       );
    //       this.cartElements.update((cart) => [...cart, productCart]);
    //     }
    //   }
    // });
  }

  public removeAllProducts(): void {
    this.setCartElements([]);
    this.navigationService.navigate(
      [],
      {
        queryParams: {
          listado_prod: null,
        },
        queryParamsHandling: 'merge',
      },
      false,
    );
  }

  public resetCart(): void {
    this.setBienDeUso(false);
    this.removeAllProducts();
    this.setShipment(null);
    this.setIdTypePayment(3);
    this.addPerceptions(null);
    this.discount.set(null);
    this.resetDiscountParams();
  }

  public removeAllSameProduct(idProduct: number): void {
    this.cartElements.update((cartElements) =>
      cartElements.filter((cartElement) => cartElement.idProduct !== idProduct),
    );
  }

  public removeMultipleProducts(idProducts: number[]): void {
    this.cartElements.update((cartElements) =>
      cartElements.filter((cartElement) => !idProducts.includes(cartElement.idProduct)),
    );
  }

  public removeProduct(idProduct: number): void {
    this.cartElements.update((cartElements) =>
      cartElements.filter((item) => item.idProduct !== idProduct),
    );
  }

  public isIdsProductsInCart(idsProducts: number[]): boolean {
    return this.getCartElements().some((cartElment) => idsProducts.includes(cartElment.idProduct));
  }

  public addModality(idShipment: number): void {
    this.setModality(idShipment);
  }

  public addShippingPrice(shipmentPrice: number): void {
    this.setShipment(shipmentPrice);
  }

  public addPostalCode(cp: string): void {
    this.setPostalCode(cp);
  }

  public addBuild(cartProducts: IProductCart[]): void {
    // TODO: descomentar para aplicar la lógica con builds
    // const nextIdBuild = Math.max(0, ...this.getCartElements().map((item) => item.idBuild || 0)) + 1;
    const build = cartProducts.map((cartProduct) => ({
      ...cartProduct,
      quantity:
        (this.cartElements()
          .find((cartElement) => cartProduct.idProduct == cartElement.idProduct)
          ?.quantity() || 0) + cartProduct.quantity,
      // idBuild: nextIdBuild,
    }));
    this.setCartElements(build);
  }

  public addCartElements(cartElements: IProductCart[]): void {
    this.setCartElements(cartElements);
  }

  public addIdTypePayment(idTypePayment: number): void {
    this.setIdTypePayment(idTypePayment);
  }

  public addPerceptions(perceptions: PercepcionesCalculadas[]): void {
    this.perceptions.set(perceptions);
  }

  public setBienDeUso(value: boolean): void {
    if (this.bienDeUso() != value) {
      this.bienDeUso.set(value);
    }
  }

  private setIdTypePayment(idTypePayment: number): void {
    if (this.idTypePayment() != idTypePayment) {
      this.idTypePayment.set(idTypePayment);
      this.cartElements().forEach((cartElement) => cartElement.setTypePayment(idTypePayment));
    }
  }

  private setModality(idModality: number): void {
    if (idModality != this.idModality()) {
      idModality = idModality ? parseInt(idModality.toString()) : null;
      this._idModality.set(idModality);
    }
  }

  private setPostalCode(cp: string): void {
    if (cp != this._postalCode()) {
      this._postalCode.set(cp);
    }
  }

  private setShipment(shipmentPrice: number): void {
    if (shipmentPrice != this.shipmentPrice()) {
      this.shipmentPrice.set(shipmentPrice === -1 ? null : shipmentPrice);
    }
  }

  public setDiscountParams(discount: { id: number; code: string }): void {
    if (discount.id !== this.discountParam()?.id && discount.code !== this.discountParam()?.code) {
      this.discountParam.set(discount);
    }
  }

  public resetDiscountParams(): void {
    this.discountParam.set(null);
  }

  public setDiscount(discount: Discount): void {
    if (
      discount.id !== this.discount()?.id &&
      discount.identificador !== this.discount()?.identificador
    ) {
      this.discount.set(new DiscountCartClass(discount));
    }
  }

  public get getCartElements(): Signal<ProductCartClass[]> {
    return this.cartElements;
  }

  public get getBienDeUso(): Signal<boolean> {
    return computed(() => this.bienDeUso());
  }
  public get getDiscountParam(): Signal<{ id: number; code: string }> {
    return this.discountParam;
  }

  public get getIdTypePayment(): Signal<number> {
    return this.idTypePayment;
  }

  public get getShipment(): Signal<number> {
    return this.shipmentPrice;
  }

  public get getPerceptions(): Signal<PercepcionesCalculadas[]> {
    return this.perceptions;
  }

  public getQuantityIdProduct(idProduct: number): Signal<number> {
    const quantity =
      this.cartElements()
        .find((cartElement) => cartElement.idProduct == idProduct)
        ?.quantity() ?? 0;
    return signal(quantity);
  }

  public getTotalQuantity(productList: Record<number, Product>): Signal<number> {
    const totalQuantity = this.cartElements().reduce(
      (sum, cartElement) => sum + (productList[cartElement.idProduct] ? cartElement.quantity() : 0),
      0,
    );

    return signal(totalQuantity);
  }

  public getTotalQuantityCartElements(): Signal<number> {
    const totalQuantity = this.cartElements().reduce(
      (sum, cartElement) => sum + cartElement.quantity(),
      0,
    );

    return signal(totalQuantity);
  }

  // TODO: lógica temporal hasta nuevo carrito
  public checkArmado(cartProduct: ProductCartClass): Observable<boolean> {
    if (SubcatesObligatorias.includes(cartProduct.idSubcategory)) {
      const productoEsLimitante = this.productoLimitante(cartProduct);
      const productoArmado = this.checkProductsInList(idsArmados);
      const instalarCCPU = this.checkProductsInList([idInstalarCCPU]);

      if (productoArmado && productoEsLimitante) {
        return this.modalAlertRemoveBuild().pipe(
          first(),
          map((result) => {
            if (result) {
              if (cartProduct.idSubcategory === SubcategoriesEnum.COOLER_CPU && instalarCCPU) {
                this.subQuantity(instalarCCPU.idProduct);
              }
              if (instalarCCPU && productoArmado.quantity() === instalarCCPU.quantity()) {
                this.subQuantity(instalarCCPU.idProduct);
              }
              this.subQuantity(productoArmado.idProduct);
            }
            return result;
          }),
        );
      }
    }
    return of(true);
  }

  public modalAlertRemoveBuild(): Observable<boolean> {
    this.dialogRef = this.dialog.open(DialogModalComponent, {
      width: '440px',
      height: 'auto',
      maxHeight: '100%',
      maxWidth: '100vw',
      disableClose: true,
      data: { type: 'noArmadoService' },
    });
    return this.dialogRef.afterClosed();
  }

  public checkProductsInList(ids_subcate: number[]): ProductCartClass | undefined {
    return this.cartElements().find((product) => ids_subcate.includes(product.idProduct));
  }

  public productoLimitante(cartProduct: ProductCartClass): boolean {
    const armado = this.checkProductsInList(idsArmados);
    return armado ? armado.quantity() === cartProduct.quantity() : false;
  }

  public subQuantity(idProduct: number): void {
    const product = this.cartElements().find((p) => p.idProduct === idProduct);
    if (product && product.quantity() > 0) {
      product.changeQuantity(product.quantity() - 1);
    }
  }

  public traerDemora() {
    return this._http.get('https://static.compragamer.com/armado/demora');
  }

  public loadReservaError(error: httpError): void {
    this.modalMessageService.openModalMessage({
      data: { message: error.description, animation: NotificationVariant.ERROR },
    });
  }
}
